import { makeStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useFormikContext } from 'formik';
import { FilledInput } from '@material-ui/core';
import { ADDRESS_TYPE } from '~/modules/common/enums';
import { useMeContext } from '~/modules/me';
import { useIsBreakpointDown } from '~/modules/common/hooks';
import SimpleDropdown from '~/modules/common/components/SimpleDropdown';
import { AddressType } from '~/types';

const useDropDownStyles = makeStyles(theme => ({
  select: {
    paddingTop: `${theme.spacing(1.5)}px !important`,
    paddingBottom: `${theme.spacing(1.5)}px !important`,
    [theme.breakpoints.down('sm')]: {
      paddingTop: `${theme.spacing(2.5)}px !important`
    }
  }
}));

export const AddressTypeDropdown = ({
  record,
  field,
  index,
  column: { addressHandlers }
}) => {
  const isMobile = useIsBreakpointDown('sm');
  const me = useMeContext();
  const { hasViewProjectBillingOptions } = me;
  const intl = useIntl();
  const {
    values: { addresses }
  } = useFormikContext();

  const dropDownClasses = useDropDownStyles();

  const selectProps = useMemo(() => {
    return {
      input: <FilledInput />,
      classes: dropDownClasses
    };
  }, [dropDownClasses]);

  const options = useMemo(() => {
    const baseOptions = [
      {
        id: AddressType.Others,
        displayText: intl.formatMessage({
          id: `projectBasicInfoCard.addressInfo.addressTypeOptions.${ADDRESS_TYPE.OTHERS}`
        })
      },
      {
        id: AddressType.Primary,
        displayText: intl.formatMessage({
          id: `projectBasicInfoCard.addressInfo.addressTypeOptions.${ADDRESS_TYPE.PRIMARY}`
        })
      }
    ];

    if (hasViewProjectBillingOptions) {
      baseOptions.unshift({
        id: AddressType.Billing,
        displayText: intl.formatMessage({
          id: `projectBasicInfoCard.addressInfo.addressTypeOptions.${ADDRESS_TYPE.BILLING}`
        })
      });
    }

    return baseOptions;
  }, [intl, hasViewProjectBillingOptions]);

  const selectedValue = useMemo(() => {
    return options.find(x => x.id === ADDRESS_TYPE[record[field]]);
  }, [field, options, record]);

  const excludedAddressTypes = new Set(
    addresses
      .filter((_, i) => i !== index)
      .map(address => ADDRESS_TYPE[address.addressType])
  );

  const handleChange = useCallback(
    value => {
      addressHandlers.onAddressChange({
        index,
        field,
        value: value.id
      });
    },
    [addressHandlers, index, field]
  );

  return (
    <SimpleDropdown
      dataQeId={`${field}`}
      fullWidth
      variant="filled"
      label={
        isMobile &&
        intl.formatMessage({
          id: 'projectBasicInfoCard.addressInfo.fields.addressType'
        })
      }
      options={options.filter(option => {
        if (
          option.id === AddressType.Primary ||
          option.id === AddressType.Billing
        ) {
          return !excludedAddressTypes.has(option.id);
        }

        return true;
      })}
      onChange={handleChange}
      value={selectedValue}
      noneOption={false}
      selectProps={selectProps}
      disableSort
    />
  );
};

AddressTypeDropdown.propTypes = {
  record: PropTypes.object.isRequired,
  field: PropTypes.string,
  index: PropTypes.number,
  column: PropTypes.object
};

export default AddressTypeDropdown;
